import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, HelpCircle, Handshake, Send, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isAdminEmail } from '../lib/admins';
import { usePromoterStatus } from '../hooks/usePromoterStatus';
import { NotificationBell } from './NotificationBell';
import { MessagesBadge } from './MessagesBadge';
import { UserMenu } from './UserMenu';
import { Logo } from './Logo';

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, userRole } = useAuth();
  const promoter = usePromoterStatus();

  // Don't show header on landing pages as requested
  if (location.pathname === '/' || location.pathname === '/exposants') return null;

  // Visiteur non connecté (ex. /home, /festival/:id — pages publiques) : pas
  // la barre "connectée" (Tableau de bord, Mes Festivals, avatar...) — avant
  // ce fix, un rôle par défaut 'stand' était affiché à tout le monde, donnant
  // l'illusion trompeuse d'un compte déjà connecté. Mais pas non plus rien :
  // juste le logo + un retour, pour ne pas laisser la page sans navigation.
  // AuthProvider bloque déjà le rendu tant que `loading` est vrai (spinner
  // plein écran), donc ici `user` est définitivement résolu.
  if (!user) {
    return (
      <header className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-[10px] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('nav.back')}
          </button>
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <Logo className="text-xl" />
          </div>
        </div>
      </header>
    );
  }

  // Le rôle 'promoter' aura son propre header/dashboard (module Affiliation).
  // Ici on retombe sur un rôle géré par ce header historique (stand/festival).
  const role: 'stand' | 'festival' = userRole === 'festival' ? 'festival' : 'stand';

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-[10px] border-b border-white/5 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        
        <div className="flex items-center space-x-8">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
             <Logo className="text-xl " />
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-semibold text-gray-400">
            <span 
              className={`hover:text-white cursor-pointer transition-colors ${location.pathname === (role === 'stand' ? '/stand/profile' : '/my-festivals') ? 'text-white' : ''}`}
              onClick={() => role === 'stand' ? navigate('/stand/profile') : navigate('/my-festivals')}
            >
              {t('nav.dashboard')}
            </span>
            <span
              className={`cursor-pointer transition-colors px-3 py-1.5 rounded-lg border border-white/5 ${location.pathname === '/home' ? 'text-white bg-white/10' : 'hover:text-white'}`}
              onClick={() => navigate('/home', { state: { role } })}
            >
              {t('nav.exploreFestivals')}
            </span>
            {role === 'festival' && (
              <span
                className={`hover:text-white cursor-pointer transition-colors ${location.pathname === '/explore-stands' ? 'text-white' : ''}`}
                onClick={() => navigate('/explore-stands')}
              >
                {t('nav.exploreStands')}
              </span>
            )}
            {role === 'stand' && (
              <span
                className={`hover:text-white cursor-pointer transition-colors ${location.pathname === '/stand/festivals' ? 'text-white' : ''}`}
                onClick={() => navigate('/stand/festivals')}
              >
                {t('nav.myFestivals')}
              </span>
            )}
            {isAdminEmail(user?.email) && (
              <span 
                className={`hover:text-white cursor-pointer transition-colors px-3 py-1.5 rounded-lg border border-white/5 bg-red-500/10 text-red-400 ${location.pathname === '/admin' ? 'bg-red-500/20 text-red-300' : ''}`}
                onClick={() => navigate('/admin')}
              >
                Admin
              </span>
            )}
          </nav>
        </div>

        <div className="flex items-center space-x-5">
          {/* Promoteur actif → raccourci soumettre un festival ; sinon → CTA affiliation */}
          {promoter.codeActive ? (
            <button
              onClick={() => navigate('/promoter/submit-festival')}
              className="hidden lg:flex items-center gap-2 text-sm font-bold bg-[#FF5A00]/10 text-[#FF5A00] hover:bg-[#FF5A00]/20 px-4 py-2 rounded-lg transition-colors"
            >
              <Send className="w-4 h-4" /> {t('nav.submitFestival')}
            </button>
          ) : (
            <button
              onClick={() => navigate('/become-promoter')}
              className="hidden lg:flex items-center gap-2 text-sm font-bold text-[#FF5A00] hover:bg-[#FF5A00]/10 px-4 py-2 rounded-lg transition-colors"
            >
              <Handshake className="w-4 h-4" /> {t('nav.becomePromoter')}
            </button>
          )}

          {role === 'festival' && (
            <button
              onClick={() => navigate('/choose-creation-method', { state: { forceNew: true } })}
              className="hidden lg:flex items-center text-sm font-bold bg-[#4b3cfa]/10 text-[#4b3cfa] hover:bg-[#4b3cfa]/20 px-4 py-2 rounded-lg transition-colors"
            >
              + {t('nav.publishCall')}
            </button>
          )}

          <div className="flex items-center space-x-4 text-gray-400">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-onboarding'))}
              className="p-1 hover:text-white transition-colors flex items-center justify-center"
              title={t('nav.startGuide') as string}
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <MessagesBadge />
            <div className="hidden md:block">
              <NotificationBell role={role} />
            </div>
          </div>
          <UserMenu role={role} promoterActive={promoter.codeActive} />
        </div>
      </div>
    </header>
  );
};

export default Header;
